import { Box, Grid, Typography } from '@mui/material';
import { EAssetType } from 'constants/enums';
import { UploadsTooltips } from 'hooks/useUpload';
import { useParams } from 'react-router-dom';

import AcCard from 'components/AcCard/AcCard';
import AcSelect from 'components/AcSelect/AcSelect';
import { Flex } from 'components/ui/flex';

import { EToasterStatus, showToast } from '../../../../utils/showToast';
import { validateImageRatio } from '../../utils';
import { CheckoutLegalText } from '../CheckoutLegalText';
import usePermissions from 'api/usePermissions';
import { checkoutTextColorOptions, defaultCheckoutThemeV2 } from '../defaults';

import 'style/forms.scss';
import Logo from './CommonFields/Logo';
import PreviewButton from './CommonFields/PreviewButton';
import { CheckoutThemeProps } from '../types';

const defaultBackgroundImage = 'White Background (default)';

const CheckoutThemeV3 = ({
  defaultValues,
  pictures,
  setFieldValue,
  handleChange,
  handleBlur,
  touched,
  values,
  setShowPreview,
  errors
}: CheckoutThemeProps) => {
  const { isSuperAdminByProject } = usePermissions();
  const { publisherId } = useParams();

  return (
    <Flex className="formContent w-full">
      <Flex direction="row" justify="between">
        <Grid container>
          <Grid item xs={5}>
            <Typography
              variant="h6"
              sx={{
                marginBottom: '12px',
                fontFamily: 'Inter, Montserrat, serif',
                fontWeight: '600'
              }}
            >
              Checkout Page
            </Typography>
            <Flex className="gap-6">
              <AcCard stackContainer={false}>
                <Grid container rowSpacing={2} columnSpacing={1.5}>
                  <Grid item xs={12}>
                    <Logo
                      defaultValues={defaultValues}
                      pictures={pictures}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <AcSelect
                        header="Font Color"
                        name="textColor"
                        value={defaultValues.textColor}
                        items={checkoutTextColorOptions}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        dataTestBoxId="checkoutTextColor"
                        dataTestListId="checkoutTextColorList"
                        optionDataTestIdPrefix="checkoutTextColorOption"
                        error={touched.textColor && Boolean(errors.textColor)}
                        helperText={
                          touched.textColor ? errors.textColor?.toString() : ''
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <AcSelect
                      header="Background Image"
                      name="backgroundImage"
                      displayEmpty
                      value={
                        values.backgroundImage ===
                          defaultCheckoutThemeV2.backgroundImage ||
                        !values.backgroundImage
                          ? defaultBackgroundImage
                          : values.backgroundImage
                      }
                      dataTestBoxId="checkoutBackgroundImage"
                      uploadBtnTestId="uploadCheckoutBackgroundImage"
                      dataTestListId="checkoutBackgroundImageList"
                      optionDataTestIdPrefix="checkoutBackgroundImageOption"
                      onClear={
                        values.backgroundImage !==
                          defaultCheckoutThemeV2.backgroundImage &&
                        values.backgroundImage !== ''
                          ? () => setFieldValue('backgroundImage', '')
                          : undefined
                      }
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('backgroundImage', url);
                        },
                        uploadType: EAssetType.BG_DESKTOP,
                        validator: async (file) => {
                          const isValid = await validateImageRatio(file, 1 / 2);
                          if (!isValid) {
                            showToast({
                              message: 'Image aspect ratio must be 1:2',
                              status: EToasterStatus.ERROR
                            });
                          }
                          return isValid;
                        },
                        uploadMessage: UploadsTooltips.BG_CHECKOUT
                      }}
                      items={[
                        {
                          content: defaultBackgroundImage,
                          key: '',
                          value: '',
                          renderFunction: () => {
                            return (
                              <Flex
                                direction="row"
                                align="center"
                                className="gap-2"
                              >
                                <Typography>
                                  {defaultBackgroundImage}
                                </Typography>
                              </Flex>
                            );
                          }
                        },
                        ...(pictures.data || [])
                          .filter((p: any) => p.type === EAssetType.BG_DESKTOP)
                          .map((picture: any) => {
                            return {
                              content: picture.name,
                              key: picture.url,
                              value: picture.url,
                              renderFunction: () => {
                                return (
                                  <Flex
                                    direction="row"
                                    align="center"
                                    className="gap-2"
                                  >
                                    <img
                                      src={picture.url}
                                      alt={picture.name}
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>{picture.name}</Typography>
                                  </Flex>
                                );
                              }
                            };
                          })
                      ]}
                      onChange={(e) => {
                        setFieldValue('backgroundImage', e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched.backgroundImage &&
                        Boolean(errors.backgroundImage)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <AcSelect
                      header="Background Image (Mobile)"
                      name="mobileBgImage"
                      displayEmpty
                      value={
                        defaultValues.mobileBgImage ||
                        'No Background (Taken from Desktop)'
                      }
                      dataTestBoxId="checkoutMobileBgImage"
                      uploadBtnTestId="uploadCheckoutMobileBgImage"
                      dataTestListId="checkoutMobileBgImageList"
                      optionDataTestIdPrefix="checkoutMobileBgImageOption"
                      onClear={
                        values.mobileBgImage
                          ? () => setFieldValue('mobileBgImage', '')
                          : undefined
                      }
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('mobileBgImage', url);
                        },
                        uploadType: EAssetType.BG_MOBILE,
                        validator: async (file) => {
                          const isValid = await validateImageRatio(file, 1 / 2);
                          if (!isValid) {
                            showToast({
                              message: 'Image aspect ratio must be 1:2',
                              status: EToasterStatus.ERROR
                            });
                          }
                          return isValid;
                        },
                        uploadMessage: UploadsTooltips.BG_CHECKOUT
                      }}
                      items={[
                        {
                          content: 'No Background (Taken from Desktop)',
                          key: '',
                          value: '',
                          renderFunction: () => {
                            return (
                              <Flex
                                direction="row"
                                align="center"
                                className="gap-2"
                              >
                                <Typography>
                                  No Background (Taken from Desktop)
                                </Typography>
                              </Flex>
                            );
                          }
                        },
                        ...(pictures.data || [])
                          .filter((p: any) => p.type === EAssetType.BG_MOBILE)
                          .map((picture: any) => {
                            return {
                              content: picture.name,
                              key: picture.url,
                              value: picture.url,
                              renderFunction: () => {
                                return (
                                  <Flex
                                    direction="row"
                                    align="center"
                                    className="gap-2"
                                  >
                                    <img
                                      src={picture.url}
                                      alt={picture.name}
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>{picture.name}</Typography>
                                  </Flex>
                                );
                              }
                            };
                          })
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.mobileBgImage && Boolean(errors.mobileBgImage)
                      }
                    />
                  </Grid>
                  {isSuperAdminByProject(publisherId || '') && (
                    <Grid item xs={12}>
                      <CheckoutLegalText
                        values={values}
                        setFieldValue={setFieldValue}
                        defaultValues={defaultValues}
                      />
                    </Grid>
                  )}
                </Grid>
              </AcCard>
            </Flex>
            <Box mt={4}>
              <PreviewButton setShowPreview={setShowPreview} />
            </Box>
          </Grid>
        </Grid>
      </Flex>
    </Flex>
  );
};

export default CheckoutThemeV3;
